import { Button } from '@chakra-ui/button'
import { Flex, Stack } from '@chakra-ui/react'
import type { InsuranceParamsLeasingDto, WizardVehicleAdditionalData } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { useInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import type { AdditionalInfoValidationSchemaContext } from 'BrokerTsLib/src/validation'
import { AdditionalInfoVehicleValidationSchema } from 'BrokerTsLib/src/validation'
import cloneDeep from 'lodash/cloneDeep'
import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import * as yup from 'yup'

import { CustomWaypoint } from '@/components/CustomWaypoint'
import ErrorSummary from '@/components/ErrorSummary'
import { PageSection } from '@/components/PageSection'
import { UnsavedFormModal } from '@/components/UnsavedFormModal'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSelectedQuote } from '@/context/SelectedQuoteContext'
import { useSidebar } from '@/context/SidebarContext'
import { useApp } from '@/hooks/useApp'
import { useNameof } from '@/hooks/useNameof'
import { useSaveAdditionalData, useUpdateLastWizardStep } from '@/hooks/useWizard'
import { Step3Sections } from '@/pages/steps/vehicle/3'

import type { PageSection as Section } from '@/layouts/components/Sidebar'
import { FormSection } from '../../../forms/FormSection'
import { AdditionalContactInfoForm } from './forms/AdditionalInsureeInfoForm/AdditionalContactInfoForm'
import { AdditionalHolderInfoForm } from './forms/AdditionalInsureeInfoForm/AdditionalHolderInfoForm'
import { AdditionalOwnerInfoForm } from './forms/AdditionalInsureeInfoForm/AdditionalOwnerInfoForm'
import { AdditionalPolicyHolderInfoForm } from './forms/AdditionalInsureeInfoForm/AdditionalPolicyHolderInfoForm'
import { VinkulantForm } from './forms/AdditionalInsureeInfoForm/VinkulantForm'
import { AdditionalVehicleInfoForm } from './forms/VehicleAdditionalInfoForm'

export type Step3FormValues = WizardVehicleAdditionalData

export type Step3FormProps = {
  data?: Step3FormValues
}

export const Step3 = ({ data }: Step3FormProps) => {
  const { product } = useApp()
  const navigate = useNavigate()
  const { insuranceParams, refetchInsuranceParams, insuranceParamsId: paramsId, isDisabled } = useInsuranceParams()
  const { mutateAsync: saveAdditionalInfo } = useSaveAdditionalData()
  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const insuranceParamsLeasing = insuranceParams as InsuranceParamsLeasingDto
  const { offer } = useSelectedQuote()

  const nameof = useNameof<Step3FormValues>()
  const personPrefix = nameof(x => x.policyHolder.person)
  const clientPrefix = nameof(x => x.policyHolder.client)

  const formDefaultValues: Step3FormValues = {
    ...data!,
    nextPage: 'next'
  }

  const schema = (context: AdditionalInfoValidationSchemaContext) => yup.object().shape({
    vehicle: AdditionalInfoVehicleValidationSchema(context)
  }) as any

  const methods = useForm<Step3FormValues>({
    values: formDefaultValues,
    resolver: yupResolver(schema({ offer: offer!, extra: false })) as any, // aby se pripadne resetovaly serverove validace
    mode: 'onBlur',
  })

  // v nevalidniho formulare se nascroluje na validationSummary
  const validationSummaryRef = useRef<HTMLDivElement>(null)
  const scrollToValidationSummary = useCallback(() => {
    setTimeout(() => {
      validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }, [validationSummaryRef])

  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    setError,
    getValues,
    reset,
    watch
  } = methods

  const insuranceParamsFormPersistance = useInsuranceParamsFormPersistance(Product.VehicleInsurance, 3, paramsId, methods)

  const submitForm = handleSubmit((data) => {
    const values = cloneDeep(data)

    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den
    if (values.policyHolder.vinkulant) {
      values.policyHolder.vinkulant.policyEndDate = formatDate(values.policyHolder.vinkulant?.policyEndDate)
      values.policyHolder.vinkulant.policyStartDate = formatDate(values.policyHolder.vinkulant?.policyStartDate)
    }

    if (values.vehicle.firstRegistrationDate)
      values.vehicle.firstRegistrationDate = formatDate(values.vehicle.firstRegistrationDate)

    if (values.vehicle.invoiceDate)
      values.vehicle.invoiceDate = formatDate(values.vehicle.invoiceDate)

    return saveAdditionalInfo(values)
      .then((response) => {
        reset(data)
        insuranceParamsFormPersistance.clear()

        if (values.nextPage == 'next') {
          if ((insuranceParams?.generalParams.lastWizardStep ?? 3) == 3) {
            updateStep({ paramsId: Number(paramsId), step: 4 })
              .then(() => refetchInsuranceParams())
          }

          // aby se stihl resetovat formular a smazal local storage pred navigaci (jinak se zobrazuje okno s neulozenymi daty)
          setTimeout(() => {
            navigate(`/steps/4/${paramsId}`)
          }, 0)
        }
      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })

        scrollToValidationSummary()
      })
      .finally(() => { })
  }, (err) => {
    scrollToValidationSummary()
  })

  const showVinkulant = getValues('policyHolder.showVinkulant')
  const { pageSections, setPageSections } = useSidebar()
  useEffect(() => {
    let i = 1
    const array: Section[] = []

    array.push(
      {
        id: 'section_' + Step3Sections.vozidlo,
        index: i,
        sectionTitle: 'Vozidlo',
        children: (
          <FormSection number={i++} sectionTitle="Vozidlo">
            <AdditionalVehicleInfoForm />
          </FormSection>
        )
      }
    )

    array.push(
      {
        id: 'section_' + Step3Sections.pojistnik,
        index: i,
        sectionTitle: 'Pojistník',
        children: (
          <FormSection number={i++} sectionTitle="Pojistník">
            <AdditionalPolicyHolderInfoForm personPrefix={personPrefix} />
          </FormSection>
        )
      }
    )

    if (product == Product.VehicleLeasingInsurance) {
      array.push(
        {
          id: 'section_' + Step3Sections.klient,
          index: i,
          sectionTitle: 'Klient',
          children: (
            <FormSection number={i++} sectionTitle="Klient">
              <AdditionalPolicyHolderInfoForm personPrefix={clientPrefix} />
            </FormSection>
          )
        }
      )
    }

    array.push(
      {
        id: 'section_' + Step3Sections.drzitel,
        index: i,
        sectionTitle: 'Držitel',
        children: (
          <FormSection number={i++} sectionTitle="Držitel/provozovatel vozidla">
            <AdditionalHolderInfoForm isDisabledChanges />
          </FormSection>
        )
      }
    )

    array.push(
      {
        id: 'section_' + Step3Sections.vlastnik,
        index: i,
        sectionTitle: 'Vlastník',
        children: (
          <FormSection number={i++} sectionTitle="Vlastník">
            <AdditionalOwnerInfoForm isDisabledChanges />
          </FormSection>
        )
      }
    )

    array.push(
      {
        id: 'section_' + Step3Sections.adresa,
        index: i,
        sectionTitle: 'Korespondenční adresa',
        children: (
          <FormSection number={i++} sectionTitle="Korespondenční adresa">
            <AdditionalContactInfoForm />
          </FormSection>
        )
      }
    )

    if (showVinkulant && product != Product.VehicleLeasingInsurance) {
      array.push(
        {
          id: 'section_' + Step3Sections.vinkulant,
          index: i,
          sectionTitle: 'Vinkulant',
          children: (
            <FormSection number={i++} sectionTitle="Vinkulant">
              <VinkulantForm />
            </FormSection>
          )
        }
      )
    }

    setPageSections(array)
  }, [product, showVinkulant])

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <Stack gap={8}>
          {pageSections.map(x => (
            <CustomWaypoint key={x.id} sectionId={x.id}>
              <PageSection id={x.id}>
                {x.children}
              </PageSection>
            </CustomWaypoint>
          ))}

          {!isDisabled &&
            < Flex justifyContent='center'>
              <Button
                type="button"
                variant="solid"
                colorScheme="green"
                isLoading={isSubmitting}
                onClick={submitForm}
              >
                Pokračovat
              </Button>
            </Flex>
          }

          <ErrorSummary errors={errors} ref={validationSummaryRef} />
          <UnsavedFormModal {...insuranceParamsFormPersistance} />
        </Stack>
      </form >
    </FormProvider >
  )
}
